import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, ArrowUpRight } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email, and message are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Request received. We'll be in touch within one business day.");
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please call us instead.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-navy text-white py-24 md:py-40 relative overflow-hidden" data-testid="contact-section">
      <div className="absolute inset-0 pointer-events-none opacity-20"
           style={{ backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px)", backgroundSize: "8.333% 100%" }} />

      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
            className="lg:col-span-7"
          >
            <h2 className="font-display font-black uppercase leading-[0.9] tracking-[-0.03em] text-5xl md:text-6xl lg:text-7xl"
                data-testid="contact-heading">
              Contact us today for your <span className="italic font-medium">free quote!</span>
            </h2>

            <div className="mt-12 space-y-8 max-w-lg">
              <a
                href="tel:2059993399"
                className="group flex items-center justify-between border-t border-white/20 pt-6"
                data-testid="contact-phone"
              >
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 font-semibold mb-2">Phone</div>
                  <div className="font-display font-black text-3xl md:text-4xl tracking-tight">205 · 999 · 3399</div>
                </div>
                <Phone className="w-6 h-6 opacity-60 group-hover:opacity-100 transition-opacity" />
              </a>

              <a
                href="mailto:peakdisposalal@gmail.com"
                className="group flex items-center justify-between border-t border-white/20 pt-6"
                data-testid="contact-email"
              >
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 font-semibold mb-2">Email</div>
                  <div className="font-display font-bold text-xl md:text-2xl tracking-tight break-all">
                    peakdisposalal@gmail.com
                  </div>
                </div>
                <Mail className="w-6 h-6 opacity-60 group-hover:opacity-100 transition-opacity shrink-0 ml-3" />
              </a>

              <div className="border-t border-white/20 pt-6" data-testid="service-areas">
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 font-semibold mb-2">Service Areas</div>
                <p className="font-display font-medium text-lg md:text-xl tracking-tight leading-snug text-white/90">
                  Calera · Alabaster · Pelham · Helena · Montevallo · Birmingham &amp; the surrounding areas
                </p>
                <p className="text-white/50 text-[13px] leading-relaxed mt-2">
                  Not sure if we cover your area? Give us a call!
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right — Form */}
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: 0.15 }}
            className="lg:col-span-5 border border-white/25 p-6 md:p-10 space-y-6 bg-navy/40 backdrop-blur-sm h-fit"
            data-testid="contact-form"
          >
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-white/60 font-semibold block mb-2">Name</label>
              <Input
                value={form.name}
                onChange={update("name")}
                required
                className="rounded-none bg-transparent border-x-0 border-t-0 border-b border-white/40 focus-visible:ring-0 focus-visible:border-white text-white placeholder:text-white/40 px-0 h-12 text-base"
                data-testid="input-name"
                placeholder="Jane Smith"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase text-white/60 font-semibold block mb-2">Email</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  required
                  className="rounded-none bg-transparent border-x-0 border-t-0 border-b border-white/40 focus-visible:ring-0 focus-visible:border-white text-white placeholder:text-white/40 px-0 h-12 text-base"
                  data-testid="input-email"
                  placeholder="you@email.com"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase text-white/60 font-semibold block mb-2">Phone</label>
                <Input
                  value={form.phone}
                  onChange={update("phone")}
                  className="rounded-none bg-transparent border-x-0 border-t-0 border-b border-white/40 focus-visible:ring-0 focus-visible:border-white text-white placeholder:text-white/40 px-0 h-12 text-base"
                  data-testid="input-phone"
                  placeholder="Optional"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-white/60 font-semibold block mb-2">Tell us about the job</label>
              <Textarea
                value={form.message}
                onChange={update("message")}
                required
                rows={4}
                className="rounded-none bg-transparent border border-white/40 focus-visible:ring-0 focus-visible:border-white text-white placeholder:text-white/40 text-base"
                data-testid="input-message"
                placeholder="Address, timeline, what needs to go..."
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-between px-6 py-5 bg-white text-navy text-[11px] tracking-[0.25em] uppercase font-bold hover:bg-transparent hover:text-white border border-white transition-colors disabled:opacity-60"
              data-testid="submit-contact"
            >
              {loading ? "Sending…" : "Send Request"}
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
